/**
 * Creates a paginator factory
 */
const createPaginator = (name, paginationContainer, changePageCallback) => {
  let paginationListContainer;
  let loadingSpinner;
  let currentPage;

  /**
   * Initializes a paginator
   */
  const init = () => {
    initLoadingSpinner();
    initPaginationListContainer();
  };

  /**
   * Initializes the loading spinner
   */
  const initLoadingSpinner = () => {
    const spinner = document.createElement('i');
    spinner.classList.add('fa', 'fa-spinner', 'fa-pulse');

    const text = document.createElement('span');
    text.innerHTML = `&nbsp;Loading ${name}...`;

    loadingSpinner = document.createElement('div');
    loadingSpinner.classList.add('text-center');
    loadingSpinner.appendChild(spinner);
    loadingSpinner.appendChild(text);

    paginationContainer.appendChild(loadingSpinner);
  };

  /**
   * Initializes the pagination list container
   */
  const initPaginationListContainer = () => {
    paginationListContainer = document.createElement('ul');
    paginationListContainer.classList.add('pagination', 'hide');
    paginationContainer.appendChild(paginationListContainer);
  };

  /**
   * Returns an array of indexes to exclude from the pagination links
   *
   * @param {object} paginationObject The pagination object
   */
  const getPaginationLinkExcludes = (paginationObject) => {
    const ellipsesIndexes = paginationObject.links
      .map((link, index) => (link.label === '...' ? index : -1))
      .filter((index) => index >= 0);

    let begin = ellipsesIndexes.some((index) => index < 7);
    let end = ellipsesIndexes.some((index) => index > 7);

    const excludes = [];

    if (begin) {
      excludes.push(2);
    }

    if (end) {
      excludes.push(12);
    }

    if (begin && !end) {
      excludes.push(...[7, 6, 5, 4].slice(paginationObject.last_page - paginationObject.current_page));
    }

    if (end && !begin) {
      excludes.push(...[7, 8, 9, 10].slice(paginationObject.current_page - 1));
    }

    return excludes;
  };

  /**
   * Filters the pagination links
   *
   * @param {object} paginationObject The pagination object
   */
  const filterPaginationLinks = (paginationObject) => {
    getPaginationLinkExcludes(paginationObject)
      .sort((a, b) => b - a)
      .forEach((index) => {
        paginationObject.links.splice(index, 1);
      });

    return paginationObject.links.map((link) => {
      link.label = link.label.replace(/&laquo;\s/g, '').replace(/\s&raquo;/g, '');

      return link;
    });
  };

  /**
   * Builds the previous page link
   *
   * @param {object} paginationObject The pagination object
   * @param {object} link             The link object
   */
  const buildPreviousPageLink = (paginationObject, link) => {
    const paginationItem = document.createElement('li');

    paginationItem.classList.add('pagination-previous');

    if (paginationObject.current_page === 1) {
      paginationItem.classList.add('disabled');
      paginationItem.innerHTML = `<span>${link.label}</span>`;
    } else {
      paginationItem.appendChild(buildPaginationButton(link));
    }

    return paginationItem;
  };

  /**
   * Builds the next page link
   *
   * @param {object} paginationObject The pagination object
   * @param {object} link             The link object
   */
  const buildNextPageLink = (paginationObject, link) => {
    const paginationItem = document.createElement('li');

    paginationItem.classList.add('pagination-next');

    if (paginationObject.current_page === paginationObject.last_page) {
      paginationItem.classList.add('disabled');
      paginationItem.innerHTML = `<span>${link.label}</span>`;
    } else {
      paginationItem.appendChild(buildPaginationButton(link));
    }

    return paginationItem;
  };

  /**
   * Builds a page link
   *
   * @param {object} link The link object
   */
  const buildPageLink = (link) => {
    const paginationItem = document.createElement('li');

    if (link.active) {
      paginationItem.classList.add('current');
      paginationItem.innerHTML = `<span class="has-white-color">${link.label}</span>`;
    } else {
      paginationItem.appendChild(buildPaginationButton(link));
    }

    return paginationItem;
  };

  /**
   * Builds a pagination button
   *
   * @param {object} link The link object
   */
  const buildPaginationButton = (link) => {
    const paginationButton = document.createElement('button');

    paginationButton.innerHTML = link.label;
    paginationButton.addEventListener('click', () => {
      const selectedPage = link.url.split('page=')[1];
      showLoadingSpinner();
      changePageCallback(selectedPage);
    });

    return paginationButton;
  };

  /**
   * Builds a pagination ellipsis item
   */
  const buildPaginationEllipsisItem = () => {
    const paginationItem = document.createElement('li');

    paginationItem.classList.add('ellipsis');

    return paginationItem;
  };

  /**
   * Returns the pagination item
   *
   * @param {object} paginationObject The pagination object
   */
  const getPaginationItem = (paginationObject, link) => {
    switch (link.label) {
      case 'Previous':
        return buildPreviousPageLink(paginationObject, link);
      case 'Next':
        return buildNextPageLink(paginationObject, link);
      case '...':
        return buildPaginationEllipsisItem();
      default:
        return buildPageLink(link);
    }
  };

  /**
   * Shows the loading spinner
   */
  const showLoadingSpinner = () => {
    paginationListContainer.classList.add('hide');
    loadingSpinner.classList.remove('hide');
  };

  /**
   * Hides the loading spinner
   */
  const hideLoadingSpinner = () => {
    loadingSpinner.classList.add('hide');
    paginationListContainer.classList.remove('hide');
  };

  init();

  return {
    /**
     * Updates the pagination container
     *
     * @param {object} paginationObject The pagination object
     */
    update: (paginationObject) => {
      paginationListContainer.innerHTML = '';

      filterPaginationLinks(paginationObject).forEach((link) => {
        paginationListContainer.appendChild(getPaginationItem(paginationObject, link));
      });
      currentPage = paginationObject.current_page;
      hideLoadingSpinner();
    },
    /**
     * Clears the pagination container
     */
    clear: () => {
      showLoadingSpinner();
      paginationListContainer.innerHTML = '';
    },
  };
};

export const paginatorFactory = { create: createPaginator };
